import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HandLandmarker, PoseLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";

import { analyzePosture, type PostureAssessment } from "./posture";
import { HabitStorage, type TrainingSample, type TrainingKind } from "./habitTypes";
import { detectCustomHabits } from "./habitDetection";

// MediaPipe Pose connections for body skeleton
const POSE_CONNECTIONS = [
  [11, 12], [11, 13], [13, 15], [12, 14], [14, 16], // Arms
  [11, 23], [12, 24], [23, 24], // Torso
  [23, 25], [25, 27], [24, 26], [26, 28], // Legs
  [0, 1], [1, 2], [2, 3], [3, 7], // Face left
  [0, 4], [4, 5], [5, 6], [6, 8], // Face right
];

// MediaPipe Hand connections for drawing finger skeleton
const HAND_CONNECTIONS = [
  [0, 1], [1, 2], [2, 3], [3, 4],           // Thumb
  [0, 5], [5, 6], [6, 7], [7, 8],           // Index
  [0, 9], [9, 10], [10, 11], [11, 12],      // Middle
  [0, 13], [13, 14], [14, 15], [15, 16],    // Ring
  [0, 17], [17, 18], [18, 19], [19, 20],    // Pinky
  [5, 9], [9, 13], [13, 17]                 // Palm
];

const HISTORY_WINDOW = 6;
const MIN_CONFIDENCE = 0.5;

interface DetectorState {
  phase: "idle" | "loading" | "running" | "error";
  message?: string;
}

interface SmoothedAssessment {
  assessment: PostureAssessment | null;
  faceTouching: boolean;
  averageConfidence: number;
  customHabitName?: string;
  customHabitConfidence?: number;
}

type HistoryEntry = {
  faceTouch: boolean;
  confidence: number;
  timestamp: number;
};

interface SlouchDetectorProps {
  mode?: "detection" | "training";
  trainingHabitId?: string | null;
  trainingKind?: TrainingKind | null;
  onTrainingComplete?: (kind: TrainingKind) => void;
  onBackToManager?: () => void;
}

export function SlouchDetector({ 
  mode = "detection", 
  trainingHabitId = null,
  trainingKind = null,
  onTrainingComplete,
  onBackToManager
}: SlouchDetectorProps = {}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const poseLandmarkerRef = useRef<PoseLandmarker | null>(null);
  const handLandmarkerRef = useRef<HandLandmarker | null>(null);
  const rafRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const historyRef = useRef<HistoryEntry[]>([]);
  const drawPoseRef = useRef<(pose: any, hands: any, ctx: CanvasRenderingContext2D) => void>(() => {});
  const updateSmoothedRef = useRef<(
    assessment: PostureAssessment | null,
    poseLandmarks: any[] | null,
    handLandmarks: any[] | null,
    videoWidth: number,
    videoHeight: number
  ) => void>(() => {});
  const lastUpdateRef = useRef<number | null>(null);
  const faceTouchSecondsRef = useRef(0);
  const trainingSamplesRef = useRef<TrainingSample[]>([]);
  const trainingCompletedRef = useRef(false);
  const isRecordingRef = useRef(false);

  const [state, setState] = useState<DetectorState>({ phase: "idle" });
  const [restartToken, setRestartToken] = useState(0);
  const [smoothed, setSmoothed] = useState<SmoothedAssessment>({
    assessment: null,
    faceTouching: false,
    averageConfidence: 0,
  });
  const [faceTouchSeconds, setFaceTouchSeconds] = useState(0);
  const [trainingCountdown, setTrainingCountdown] = useState<number | null>(null);
  const [capturedSamples, setCapturedSamples] = useState(0);
  const TARGET_SAMPLES = 30; // Capture 30 frames during training

  const skeletonColor = useMemo(() => (smoothed.faceTouching ? "#fbbf24" : "#38bdf8"), [smoothed.faceTouching]);

  const cleanup = useCallback(async () => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    if (poseLandmarkerRef.current) {
      poseLandmarkerRef.current.close();
      poseLandmarkerRef.current = null;
    }

    if (handLandmarkerRef.current) {
      handLandmarkerRef.current.close();
      handLandmarkerRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    historyRef.current = [];
    lastUpdateRef.current = null;
    faceTouchSecondsRef.current = 0;
    setFaceTouchSeconds(0);
    trainingSamplesRef.current = [];
    trainingCompletedRef.current = false;
    isRecordingRef.current = false;
  }, []);

  const drawPose = useCallback(
    (pose: any, hands: any, ctx: CanvasRenderingContext2D) => {
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

      const videoWidth = ctx.canvas.width;
      const videoHeight = ctx.canvas.height;

      ctx.lineWidth = 4;
      ctx.lineCap = "round";
      ctx.strokeStyle = `${skeletonColor}AA`;
      ctx.fillStyle = skeletonColor;

      // Draw body skeleton from MediaPipe Pose landmarks
      if (pose?.landmarks?.[0]) {
        const landmarks = pose.landmarks[0];

        // Draw body connections
        for (const connection of POSE_CONNECTIONS) {
          const i = connection[0];
          const j = connection[1];
          if (i === undefined || j === undefined) continue;
          const point1 = landmarks[i];
          const point2 = landmarks[j];
          if (!point1 || !point2) continue;
          if ((point1.visibility ?? 0) < 0.5 || (point2.visibility ?? 0) < 0.5) continue;

          ctx.beginPath();
          ctx.moveTo(point1.x * videoWidth, point1.y * videoHeight);
          ctx.lineTo(point2.x * videoWidth, point2.y * videoHeight);
          ctx.stroke();
        }

        // Draw body joints
        for (const landmark of landmarks) {
          if ((landmark.visibility ?? 0) < 0.5) continue;
          ctx.beginPath();
          ctx.arc(landmark.x * videoWidth, landmark.y * videoHeight, 6, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // Draw hand landmarks and finger skeleton
      if (hands?.landmarks) {
        ctx.lineWidth = 2;

        for (const landmarks of hands.landmarks) {
          // Draw finger connections
          ctx.strokeStyle = `${skeletonColor}DD`;
          for (const connection of HAND_CONNECTIONS) {
            const i = connection[0];
            const j = connection[1];
            if (i === undefined || j === undefined) continue;
            const point1 = landmarks[i];
            const point2 = landmarks[j];
            if (!point1 || !point2) continue;

            ctx.beginPath();
            ctx.moveTo(point1.x * videoWidth, point1.y * videoHeight);
            ctx.lineTo(point2.x * videoWidth, point2.y * videoHeight);
            ctx.stroke();
          }

          // Draw finger joints
          ctx.fillStyle = skeletonColor;
          for (const landmark of landmarks) {
            ctx.beginPath();
            ctx.arc(landmark.x * videoWidth, landmark.y * videoHeight, 3, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
    },
    [skeletonColor]
  );

  useEffect(() => {
    drawPoseRef.current = drawPose;
  }, [drawPose]);

  const updateSmoothed = useCallback((
    assessment: PostureAssessment | null,
    poseLandmarks: any[] | null,
    handLandmarks: any[] | null,
    videoWidth: number,
    videoHeight: number
  ) => {
    const history = historyRef.current;
    const now = performance.now();
    const last = lastUpdateRef.current ?? now;
    const deltaSeconds = Math.max(0, (now - last) / 1000);
    lastUpdateRef.current = now;

    const faceTouchFrame = Boolean(assessment && assessment.handTouchingFace && assessment.confidence >= MIN_CONFIDENCE);
    const confidence = assessment?.confidence ?? 0;

    history.push({ faceTouch: faceTouchFrame, confidence, timestamp: now });
    while (history.length > HISTORY_WINDOW) {
      history.shift();
    }

    if (faceTouchFrame) {
      faceTouchSecondsRef.current += deltaSeconds;
    }

    const faceTouching = history.some(entry => entry.faceTouch);
    const averageConfidence =
      history.length > 0 ? history.reduce((acc, entry) => acc + entry.confidence, 0) / history.length : 0;

    // Check for custom habits if in detection mode
    let customHabitName: string | undefined;
    let customHabitConfidence: number | undefined;
    
    if (mode === "detection" && poseLandmarks && handLandmarks) {
      const enabledHabits = HabitStorage.getEnabledHabits();
      if (enabledHabits.length > 0) {
        const customHabitResult = detectCustomHabits(
          poseLandmarks,
          handLandmarks,
          enabledHabits,
          videoWidth,
          videoHeight
        );
        
        if (customHabitResult.habit) {
          customHabitName = customHabitResult.habit.name;
          customHabitConfidence = customHabitResult.confidence;
        }
      }
    }

    setSmoothed({ 
      assessment, 
      faceTouching, 
      averageConfidence,
      customHabitName,
      customHabitConfidence
    });

    setFaceTouchSeconds(prev => {
      const next = faceTouchSecondsRef.current;
      return Math.abs(next - prev) > 0.05 ? next : prev;
    });
  }, [mode]);

  useEffect(() => {
    updateSmoothedRef.current = updateSmoothed;
  }, [updateSmoothed]);

  // Handle training countdown
  useEffect(() => {
    if (mode === "training" && trainingHabitId && trainingKind) {
      // Initialize training state
      trainingSamplesRef.current = [];
      setCapturedSamples(0);
      trainingCompletedRef.current = false;
      isRecordingRef.current = false;
      setTrainingCountdown(3);
      
      const interval = setInterval(() => {
        setTrainingCountdown(prev => {
          if (prev === null || prev <= 1) {
            clearInterval(interval);
            isRecordingRef.current = true;
            return null;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(interval);
    } else {
      setTrainingCountdown(null);
      setCapturedSamples(0);
      trainingSamplesRef.current = [];
      trainingCompletedRef.current = false;
      isRecordingRef.current = false;
    }
  }, [mode, trainingHabitId, trainingKind]);

  useEffect(() => {
    let mounted = true;

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        historyRef.current = [];
        lastUpdateRef.current = performance.now();
      }
    };

    async function start() {
      setState({ phase: "loading", message: "Requesting camera access and loading model..." });

      try {
        historyRef.current = [];
        faceTouchSecondsRef.current = 0;
        setFaceTouchSeconds(0);
        lastUpdateRef.current = null;

        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("getUserMedia is not supported in this browser.");
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "user",
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });

        if (!mounted) {
          stream.getTracks().forEach(track => track.stop());
          return;
        }

        streamRef.current = stream;

        const video = videoRef.current;
        if (!video) throw new Error("Video element missing");
        video.srcObject = stream;

        await video.play();

        if (!mounted) return;

        // Initialize MediaPipe Vision tasks
        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.22-rc.20250304/wasm"
        );

        // Initialize Pose Landmarker
        const poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task",
            delegate: "GPU",
          },
          runningMode: "VIDEO",
          numPoses: 1,
          minPoseDetectionConfidence: 0.3,
          minPosePresenceConfidence: 0.3,
          minTrackingConfidence: 0.3,
        });
        poseLandmarkerRef.current = poseLandmarker;

        // Initialize Hand Landmarker
        const handLandmarker = await HandLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: "https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task",
            delegate: "GPU",
          },
          numHands: 2,
          runningMode: "VIDEO",
          minHandDetectionConfidence: 0.3,
          minHandPresenceConfidence: 0.3,
          minTrackingConfidence: 0.3,
        });
        handLandmarkerRef.current = handLandmarker;

        const canvas = canvasRef.current;
        if (!canvas) throw new Error("Canvas element missing");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Failed to get canvas context");

        const render = async () => {
          if (!mounted) {
            return;
          }

          if (document.hidden) {
            lastUpdateRef.current = performance.now();
            rafRef.current = requestAnimationFrame(render);
            return;
          }

          if (!videoRef.current || !poseLandmarkerRef.current || !handLandmarkerRef.current) {
            lastUpdateRef.current = performance.now();
            rafRef.current = requestAnimationFrame(render);
            return;
          }

          try {
            const now = performance.now();

            // Detect body pose
            const poseResults = poseLandmarkerRef.current.detectForVideo(videoRef.current, now);

            // Detect hands
            const handResults = handLandmarkerRef.current.detectForVideo(videoRef.current, now);

            drawPoseRef.current(poseResults, handResults, ctx);

            const assessment =
              poseResults?.landmarks?.[0]
                ? analyzePosture(poseResults.landmarks[0], {
                    handLandmarks: handResults?.landmarks,
                    videoWidth: ctx.canvas.width,
                    videoHeight: ctx.canvas.height,
                  })
                : null;
            
            // Handle training mode - only capture after countdown finishes
            if (
              mode === "training" &&
              trainingHabitId &&
              trainingKind &&
              isRecordingRef.current &&
              !trainingCompletedRef.current &&
              poseResults?.landmarks?.[0]
            ) {
              const sample: TrainingSample = {
                timestamp: Date.now(),
                poseLandmarks: poseResults.landmarks[0],
                handLandmarks: handResults?.landmarks || [],
                videoWidth: ctx.canvas.width,
                videoHeight: ctx.canvas.height,
              };
              trainingSamplesRef.current.push(sample);
              setCapturedSamples(trainingSamplesRef.current.length);
              
              // Stop after capturing TARGET_SAMPLES
              if (trainingSamplesRef.current.length >= TARGET_SAMPLES && !trainingCompletedRef.current) {
                trainingCompletedRef.current = true;
                isRecordingRef.current = false;
                HabitStorage.addTrainingSamples(trainingHabitId, trainingSamplesRef.current, trainingKind);
                trainingSamplesRef.current = [];
                onTrainingComplete?.(trainingKind);
              }
            }
            
            updateSmoothedRef.current(
              assessment,
              poseResults?.landmarks?.[0] || null,
              handResults?.landmarks || null,
              ctx.canvas.width,
              ctx.canvas.height
            );
          } catch (err) {
            console.error("Pose estimation error", err);
          }

          rafRef.current = requestAnimationFrame(render);
        };

        document.addEventListener("visibilitychange", handleVisibility);
        setState({ phase: "running" });
        lastUpdateRef.current = performance.now();
        rafRef.current = requestAnimationFrame(render);
      } catch (error) {
        console.error(error);
        document.removeEventListener("visibilitychange", handleVisibility);
        await cleanup();
        if (!mounted) return;

        const message =
          error instanceof Error
            ? error.message
            : "Unexpected error during camera or model initialization.";
        setState({ phase: "error", message });
      }
    }

    start();

    return () => {
      mounted = false;
      document.removeEventListener("visibilitychange", handleVisibility);
      cleanup();
    };
  }, [cleanup, restartToken]);

  const retry = () => {
    setSmoothed({ assessment: null, faceTouching: false, averageConfidence: 0 });
    faceTouchSecondsRef.current = 0;
    setFaceTouchSeconds(0);
    lastUpdateRef.current = null;
    setState({ phase: "idle" });
    setRestartToken(token => token + 1);
  };

  const { assessment, faceTouching, averageConfidence, customHabitName, customHabitConfidence } = smoothed;

  const status = useMemo(() => {
    // Priority 0: Custom trained habits (highest priority when detected with high confidence)
    if (customHabitName && customHabitConfidence && customHabitConfidence >= 0.7) {
      return {
        label: `${customHabitName} detected`,
        border: "border-pink-400/70",
        text: "text-pink-300",
        message: `Your custom habit "${customHabitName}" was detected. Stay aware and try to break the pattern!`,
      };
    }

    // Priority 1: Face-touching behaviors
    if (faceTouching && assessment?.behavior) {
      const behaviors = {
        "nose-picking": {
          label: "Nose picking detected",
          border: "border-red-400/70",
          text: "text-red-300",
          message: "Nose picking can spread bacteria and cause irritation. Try keeping your hands occupied.",
        },
        "nail-biting": {
          label: "Nail biting detected",
          border: "border-orange-400/70",
          text: "text-orange-300",
          message: "Nail biting can damage your nails and teeth. Try redirecting your hands away from your mouth.",
        },
        "face-touching": {
          label: "Face touching detected",
          border: "border-amber-400/70",
          text: "text-amber-300",
          message: "Try resting your hands away from your face to break automatic touch habits.",
        },
      };
      return behaviors[assessment.behavior] || behaviors["face-touching"];
    }

    if (faceTouching) {
      return {
        label: "Face touch detected",
        border: "border-amber-400/70",
        text: "text-amber-300",
        message: "Try resting your hands away from your face to break automatic touch habits.",
      };
    }

    // Priority 2: Custom habits with lower confidence
    if (customHabitName && customHabitConfidence && customHabitConfidence >= 0.5) {
      return {
        label: `${customHabitName} possibly detected`,
        border: "border-pink-400/50",
        text: "text-pink-300/80",
        message: `Possible "${customHabitName}" habit detected. Keep monitoring!`,
      };
    }

    // Priority 3: Bad posture (only shown when face is clear)
    if (assessment?.postureQuality?.isBadPosture && assessment.postureQuality.postureConfidence >= MIN_CONFIDENCE) {
      return {
        label: "Bad posture detected",
        border: "border-purple-400/70",
        text: "text-purple-300",
        message: "Your head is leaning forward. Sit up straight and align your ears over your shoulders to improve your posture.",
      };
    }

    // All clear
    return {
      label: "All clear",
      border: "border-sky-400/60",
      text: "text-sky-300",
      message: "Hands are away from your face and posture looks good. Keep it up!",
    };
  }, [faceTouching, assessment?.behavior, assessment?.postureQuality, customHabitName, customHabitConfidence]);

  const formatSeconds = useCallback((value: number) => {
    if (!Number.isFinite(value)) return "0.0s";
    if (value >= 60) {
      const minutes = Math.floor(value / 60);
      const seconds = Math.round(value % 60);
      return `${minutes}:${seconds.toString().padStart(2, "0")} min`;
    }
    return `${value.toFixed(1)}s`;
  }, []);

  const resetFaceTouchCounter = useCallback(() => {
    faceTouchSecondsRef.current = 0;
    setFaceTouchSeconds(0);
  }, []);

  const activeHandLabel = assessment?.metrics.activeHand
    ? `${assessment.metrics.activeHand.charAt(0).toUpperCase()}${assessment.metrics.activeHand.slice(1)}`
    : "—";

  const touchingFingerLabel = assessment?.metrics.touchingFinger
    ? assessment.metrics.touchingFinger.charAt(0).toUpperCase() + assessment.metrics.touchingFinger.slice(1)
    : "—";

  const faceRegionLabel = assessment?.metrics.faceRegion
    ? assessment.metrics.faceRegion.charAt(0).toUpperCase() + assessment.metrics.faceRegion.slice(1)
    : "—";

  const behaviorLabel = assessment?.behavior
    ? assessment.behavior
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    : "—";

  const behaviorColor = assessment?.behavior === "nose-picking"
    ? "text-red-400"
    : assessment?.behavior === "nail-biting"
    ? "text-orange-400"
    : assessment?.behavior === "face-touching"
    ? "text-yellow-400"
    : "text-white/80";

  const trainingTitle = trainingKind === "neutral" ? "Record baseline" : "Record habit";
  const trainingPrompt = trainingKind === "neutral"
    ? "Keep your hands away from your face and sit naturally."
    : "Perform the habit action near your face (e.g., bring your fingers to your mouth).";
  const trainingProgressLabel = trainingKind === "neutral" ? "baseline" : "habit";

  const habitName = trainingHabitId ? HabitStorage.getHabit(trainingHabitId)?.name : null;

  return (
    <div className="relative w-full max-w-[720px] mx-auto">
      {/* Back button for training mode */}
      {mode === "training" && onBackToManager && (
        <button
          onClick={onBackToManager}
          className="mb-4 flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-white transition"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Habits
        </button>
      )}

      <div className="relative rounded-3xl overflow-hidden bg-black aspect-video border border-white/10 shadow-xl">
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          className="absolute inset-0 h-full w-full object-cover opacity-75"
        />
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
        
        {/* Training countdown overlay */}
        {mode === "training" && trainingCountdown !== null && trainingCountdown > 0 && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 bg-black/80 text-white">
            <div className="text-8xl font-black animate-pulse">{trainingCountdown}</div>
            <div className="text-xl font-semibold">{trainingTitle}</div>
            <div className="text-base text-white/70 max-w-sm text-center px-6">{trainingPrompt}</div>
            {habitName && <div className="text-lg text-white/60">Habit: {habitName}</div>}
          </div>
        )}

        {/* Training progress overlay */}
        {mode === "training" && trainingCountdown === null && capturedSamples < TARGET_SAMPLES && (
          <div className="absolute top-6 left-1/2 transform -translate-x-1/2 bg-black/70 backdrop-blur px-6 py-3 rounded-full border border-amber-400/50">
            <div className="text-center">
              <div className="text-amber-300 font-bold text-lg">Recording {trainingProgressLabel} samples…</div>
              <div className="text-white/80 text-sm mt-1">
                {capturedSamples} / {TARGET_SAMPLES} samples captured
              </div>
            </div>
          </div>
        )}
        
        {state.phase !== "running" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/70 text-white text-center px-6">
            <p className="text-lg font-semibold">
              {state.phase === "error" ? state.message ?? "We hit a snag." : state.message ?? "Preparing detector..."}
            </p>
            {state.phase === "error" && (
              <button
                type="button"
                onClick={retry}
                className="px-4 py-2 bg-white text-black rounded-full font-semibold hover:bg-white/80 transition"
              >
                Try again
              </button>
            )}
          </div>
        )}
      </div>

      {mode === "training" && (
        <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-4 text-sm text-white/80">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 text-white">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-amber-400/20 border border-amber-300/40 text-xs font-semibold uppercase tracking-wide text-amber-200">
                Step
              </span>
              <span className="font-semibold uppercase tracking-wide text-white/80">{trainingTitle}</span>
            </div>
            <div>
              <p>{trainingPrompt}</p>
              <p className="mt-1 text-white/60">Tip: capture 30 clean frames. If motion is shaky, add another round.</p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-3 md:grid-cols-2">
        <div className={`rounded-2xl border p-4 backdrop-blur bg-white/5 ${status.border}`}>
          <p className="text-sm uppercase tracking-wide text-white/70">Face touch status</p>
          <p className={`text-2xl font-bold mt-2 ${status.text}`}>
            {status.label}
          </p>
          <p className="mt-2 text-sm text-white/60">
            {status.message}
          </p>
          {faceTouching && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-amber-200/80">
              <span className="rounded-full border border-amber-400/60 bg-amber-400/10 px-3 py-1 font-semibold uppercase tracking-wider">
                HABIT ALERT
              </span>
              <span>Hands near your face can trigger touch habits like nail biting or skin picking.</span>
            </div>
          )}
          {!faceTouching && assessment?.postureQuality?.isBadPosture && assessment.postureQuality.postureConfidence >= MIN_CONFIDENCE && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-purple-200/80">
              <span className="rounded-full border border-purple-400/60 bg-purple-400/10 px-3 py-1 font-semibold uppercase tracking-wider">
                POSTURE ALERT
              </span>
              <span>Forward head posture can lead to neck and back pain over time.</span>
            </div>
          )}
        </div>
        <div className="rounded-2xl border border-white/10 p-4 backdrop-blur bg-white/5 text-sm text-white/80 flex flex-col gap-2">
          <div className="flex justify-between">
            <span>Behavior detected</span>
            <span className={`font-bold ${behaviorColor}`}>{behaviorLabel}</span>
          </div>
          <div className="flex justify-between">
            <span>Face region</span>
            <span className="font-semibold">{faceRegionLabel}</span>
          </div>
          <div className="flex justify-between">
            <span>Active hand</span>
            <span className="font-semibold">{activeHandLabel}</span>
          </div>
          <div className="flex justify-between">
            <span>Touching finger</span>
            <span className="font-semibold">{touchingFingerLabel}</span>
          </div>
          <div className="flex justify-between">
            <span>Hand-to-face distance</span>
            <span className="font-semibold">
              {assessment ? `${(assessment.metrics.handToFaceDistanceRatio * 100).toFixed(1)}%` : "—"}
            </span>
          </div>
          <div className="h-px bg-white/10 my-1" />
          <div className="flex justify-between">
            <span>Posture quality</span>
            <span className={`font-semibold ${assessment?.postureQuality?.isBadPosture ? "text-purple-400" : "text-green-400"}`}>
              {assessment?.postureQuality?.isBadPosture ? "Bad" : "Good"}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Head forward angle</span>
            <span className="font-semibold">
              {assessment?.postureQuality?.headForwardAngle !== null && assessment?.postureQuality?.headForwardAngle !== undefined
                ? `${assessment.postureQuality.headForwardAngle.toFixed(1)}°`
                : "—"}
            </span>
          </div>
          <div className="h-px bg-white/10 my-1" />
          <div className="flex justify-between">
            <span>Average detection confidence</span>
            <span className="font-semibold">{averageConfidence.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span>Latest frame confidence</span>
            <span className="font-semibold">{assessment ? assessment.confidence.toFixed(2) : "—"}</span>
          </div>
          <div className="flex justify-between">
            <span>Face touch time</span>
            <span className="font-semibold">{formatSeconds(faceTouchSeconds)}</span>
          </div>
          <button
            type="button"
            onClick={resetFaceTouchCounter}
            className="mt-3 self-start rounded-full border border-white/20 px-3 py-1 text-xs uppercase tracking-wider text-white/70 transition hover:border-white/40"
          >
            Reset face touch timer
          </button>
        </div>
      </div>

      <footer className="mt-4 text-xs text-white/40 leading-relaxed">
        Your video stays on this device; face touch analysis runs entirely in your browser using TensorFlow.js. Lighting
        and camera placement affect accuracy. This demo can’t diagnose medical issues.
      </footer>
    </div>
  );
}
